import classNames from 'classnames';
import { useAtom, useAtomValue } from 'jotai';

import { Flex, If, Text } from '@/lib/v2/components';
import { ClockDisplay } from '@/lib/v3/components/ClockDisplay';
import { LanguageAccount } from '@/lib/v3/components/ClockDisplay/ClockDisplay';

import { useUserData } from '@/src/application/hooks/useUserData';

import { atomDisableEditAction } from '@/modules/CampaignsModule/atoms/actionEmailAtom';
import { atomDeliveryType } from '@/modules/CampaignsModule/atoms/actionsAtom';
import {
  DELIVERY_OPTIONS,
  DeliveryOption,
} from '@/modules/CampaignsModule/interfaces/CampaignActions';

interface WhatsAppSchedulingProps {
  deliveryOptions: DeliveryOption[];
  loading?: boolean;
}

const WhatsAppScheduling = ({ deliveryOptions, loading }: WhatsAppSchedulingProps) => {
  const [userData] = useUserData();

  const [deliveryTypeSelected, setDeliveryTypeSelected] = useAtom(atomDeliveryType);
  const disableEditAction = useAtomValue(atomDisableEditAction);

  const handleOptionClick = (id: string) => {
    if (disableEditAction) return;
    setDeliveryTypeSelected(id as DELIVERY_OPTIONS);
  };

  const optionSelectedClass = classNames('cursor-default py-9 border border-transparent', {
    '!border-emblue-primary': deliveryOptions.length > 1,
  });

  return (
    <Flex
      column
      withGap
      alignment="start"
      className="min-h-[calc(100dvh-145px)] px-32 py-10"
      gapSize="medium"
      itemAlignment="center"
    >
      {deliveryOptions.map(({ id, icon, title, description, children }) => {
        const isOptionSelected = id === deliveryTypeSelected;
        return (
          <div key={id} className="w-full max-w-screen-lg">
            {/*eslint-disable-next-line jsx-a11y/click-events-have-key-events*/}
            <div
              className={`w-full rounded-lg px-12 ${
                isOptionSelected
                  ? optionSelectedClass
                  : 'cursor-pointer border py-9 transition duration-200 ease-out hover:border-emblue-primary hover:bg-gray-50 hover:text-emblue-disabled'
              }`}
              role="button"
              tabIndex={0}
              onClick={() => handleOptionClick(id)}
            >
              <div
                className={`flex items-start justify-start gap-4 ${
                  isOptionSelected ? 'grayscale-0' : 'grayscale'
                }`}
              >
                {icon}
                <div className="flex flex-col">
                  <Text
                    className={`!text-18 ${
                      isOptionSelected ? '!text-emblue-primary' : '!text-emblue-subheadings'
                    }`}
                    fontWeight="bold"
                    variant="text"
                  >
                    {title}
                  </Text>
                  <Text className="!text-14" variant="sub-headline">
                    {description}
                  </Text>
                  <If
                    condition={
                      isOptionSelected &&
                      userData &&
                      Boolean(userData.timeOffset) &&
                      Boolean(userData.language)
                    }
                  >
                    <ClockDisplay
                      gmt={Number(userData.timeOffset)}
                      language={userData.language as LanguageAccount}
                    />
                  </If>
                </div>
              </div>
              {isOptionSelected ? (
                <div
                  className={`${isOptionSelected ? 'flex' : 'hidden'} flex-col gap-4`}
                  id={`box-${id}`}
                >
                  {loading ? (
                    <div className="w-full pt-12">
                      <section className="animate-pulse">
                        <div className="flex gap-4">
                          <div>
                            <div className="mb-3 h-3 w-[100px] rounded-full bg-gray-300"></div>
                            <div className="h-3 w-200 rounded-full bg-gray-300 "></div>
                          </div>
                          <div>
                            <div className="mb-3 h-3 w-[100px] rounded-full bg-gray-300"></div>
                            <div className="h-3 w-200 rounded-full bg-gray-300 "></div>
                          </div>
                        </div>
                      </section>
                    </div>
                  ) : (
                    children
                  )}
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </Flex>
  );
};

export default WhatsAppScheduling;
